import { ProtectedRoute } from "@/components/ProtectedRoute";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Loader2,
} from "lucide-react";
import { useState, useEffect, useCallback, useRef } from "react";
import { apiClient } from "@/lib/api";
import { usePlanAndCredits } from "@/hooks/use-plan-and-credits";
import { useSubscription } from "@/hooks/use-subscription";
import { useToast } from "@/components/ui/use-toast";
import APIKeys from "@/pages/dashboard/api-keys";
import { PlanCreditsSummary } from "@/components/plan-credits-summary";
import { AgentSetupCard } from "@/components/agent-setup-card";

export default function DashboardOverview() {
  const [apiKeys, setApiKeys] = useState<any[]>([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(true);
  const [showFreePlanPrompt, setShowFreePlanPrompt] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);

  // Use ref to track if we've already loaded API keys
  const hasLoadedApiKeys = useRef(false);

  const { toast } = useToast();

  // Use the hook only once
  const { currentPlan, creditsData, loading } =
    usePlanAndCredits();

  const { subscription } = useSubscription();

  // Simplified API keys loading function without dependencies
  const loadApiKeys = useCallback(async () => {
    if (hasLoadedApiKeys.current) return; // Prevent multiple loads

    try {
      setApiKeysLoading(true);
      const response = await apiClient.getApiKeys();
      setApiKeys(response.apiKeys || []);
      hasLoadedApiKeys.current = true;

      // Show free plan prompt if no API keys and no active subscription
      if (
        (!response.apiKeys || response.apiKeys.length === 0) &&
        !currentPlan
      ) {
        setShowFreePlanPrompt(true);
      }
    } catch (error) {
      console.error("Failed to load API keys:", error);
    } finally {
      setApiKeysLoading(false);
    }
  }, []); // Remove currentPlan dependency

  // Load API keys only once on mount
  useEffect(() => {
    loadApiKeys();
  }, [loadApiKeys]);

  // Separate effect to handle free plan prompt based on currentPlan changes
  useEffect(() => {
    if (hasLoadedApiKeys.current && apiKeys.length === 0 && !currentPlan) {
      setShowFreePlanPrompt(true);
    } else if (currentPlan || apiKeys.length > 0) {
      setShowFreePlanPrompt(false);
    }
  }, [currentPlan, apiKeys.length]);

  const handleSubscribeFreePlan = useCallback(async () => {
    try {
      setIsSubscribing(true);
      const result = await apiClient.refillFreeCredits();

      if (result.alreadyClaimed) {
        toast({
          title: "Already claimed",
          description: result.message,
          variant: "default",
        });
      } else {
        console.log("Free credits claimed and API key generated!", result);
        setShowFreePlanPrompt(false);

        // Instead of full page reload, just refresh the data
        hasLoadedApiKeys.current = false;
        await loadApiKeys();
      }
    } catch (error) {
      console.error("Trial plan subscription error:", error);
      toast({
        title: "Error",
        description: "Failed to refill free credits. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubscribing(false);
    }
  }, [toast, loadApiKeys]);

  const combinedLoading = loading || apiKeysLoading;

  if (combinedLoading) {
    return (
      <ProtectedRoute>
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center space-x-2">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span>Loading dashboard...</span>
          </div>
        </div>
      </ProtectedRoute>
    );
  }

  const primaryApiKey = apiKeys?.[0]?.unkey || apiKeys?.[0]?.apiKey || apiKeys?.[0]?.key || apiKeys?.[0]?.key_id;

  return (
    <ProtectedRoute>
      <div className="dashboard-page">
        {/* Header */}
        <div>
          <h1 className="dashboard-page-title">Overview</h1>
          <p className="dashboard-page-description">
            Monitor your API usage and manage your account
          </p>
        </div>

        {/* Free Plan Refill Prompt (includes API key generation) */}
        {showFreePlanPrompt && (
          <Card className="overflow-hidden border-stone-200/70 bg-white/80 shadow-sm">
            <CardContent className="p-6">
              <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="mb-2 inline-flex rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-emerald-700">
                    Free trial
                  </div>
                  <h3 className="text-lg font-semibold text-stone-950">
                    Start with Trial Plan
                  </h3>
                  <p className="mt-1 text-sm text-stone-600">
                    Claim this month's free credits and get your API key.
                  </p>
                </div>
                <Button
                  onClick={handleSubscribeFreePlan}
                  disabled={isSubscribing}
                  className="bg-[#1f2937] text-white hover:bg-stone-700"
                >
                  {isSubscribing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Setting up...
                    </>
                  ) : (
                    "Refill Free Credits"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {(currentPlan || creditsData.remaining > 0) && (
          <PlanCreditsSummary
            currentPlan={currentPlan}
            creditsData={creditsData}
            subscription={subscription}
            manageLabel={currentPlan ? "Manage Plan" : "Upgrade Plan"}
          />
        )}

        <APIKeys apiKeys={apiKeys} />

        <AgentSetupCard apiKey={primaryApiKey} />

        {/* <ApiKeyUsage /> */}
      </div>
    </ProtectedRoute>
  );
}
